import React, { useRef } from 'react';
import { motion, MotionValue, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import type { VentureSpread } from '../../../data/ventureStory';
type EditorialSpreadProps = {
  spread: VentureSpread;
};
const ease = [0.22, 1, 0.36, 1] as const;
const depthShadow = 'shadow-[0_50px_100px_-30px_rgba(0,0,0,0.85)]';
export function EditorialSpread({
  spread
}: EditorialSpreadProps) {
  return spread.layout === 'full' ? <FullSpread spread={spread} data-id="element-1256" data-mp="2q337eese2bm0" /> : <SplitSpread spread={spread} data-id="element-1257" data-mp="2q2teojfylqcm" />;
}

// A magazine spread: one large frame that tilts toward the cursor, a smaller print floating over its corner, and the text beside
function SplitSpread({
  spread
}: EditorialSpreadProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const k = reduce ? 0 : 1;
  const mainY = useTransform(scrollYProgress, [0, 1], [`${-6 * k}%`, `${6 * k}%`]);
  const insetY = useTransform(scrollYProgress, [0, 1], [120 * k, -120 * k]);
  const numeralY = useTransform(scrollYProgress, [0, 1], [80 * k, -80 * k]);
  const right = spread.align === 'right';

  // Cursor tilt
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const rotY = useSpring(useTransform(px, [-0.5, 0.5], [-4 * k, 4 * k]), {
    stiffness: 80,
    damping: 18
  });
  const rotX = useSpring(useTransform(py, [-0.5, 0.5], [3 * k, -3 * k]), {
    stiffness: 80,
    damping: 18
  });
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - r.left) / r.width - 0.5);
    py.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    px.set(0);
    py.set(0);
  };
  return <section ref={ref} aria-labelledby={`spread-${spread.index}`} className="relative px-6 py-24 md:px-16 md:py-36" data-id="element-1258" data-mp="1k56blooy5z1m">
      <div className="mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10" data-id="element-1259" data-mp="1p5ahylw55de4">
        <div className={`relative lg:col-span-8 [perspective:1400px] ${right ? 'lg:order-2' : ''}`} onPointerMove={onMove} onPointerLeave={onLeave} data-id="element-1260" data-mp="3qt6otexiolkx">
          <motion.div initial={{
          clipPath: reduce ? 'inset(0% 0% 0% 0%)' : right ? 'inset(0% 0% 0% 18%)' : 'inset(0% 18% 0% 0%)',
          opacity: 0.4
        }} whileInView={{
          clipPath: 'inset(0% 0% 0% 0%)',
          opacity: 1
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 1.5,
          ease
        }} style={{
          rotateX: rotX,
          rotateY: rotY
        }} className={`group relative aspect-[4/5] overflow-hidden sm:aspect-[16/11] ${depthShadow}`} data-id="element-1261" data-mp="3h3n403q8uhnf">
            <div className="absolute inset-0 transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.04]" data-id="element-1262" data-mp="2u3paycodrt85">
              <motion.img src={spread.image} alt={spread.alt} style={{
              y: mainY
            }} className="absolute left-[-8%] top-[-8%] h-[116%] w-[116%] max-w-none object-cover" data-id="element-1263" data-mp="wdxrvygxd9yf" />
            </div>
            <span className="absolute bottom-5 left-5 text-[13px] font-light text-paper/85" data-id="element-1264" data-mp="2f8sig1ewmuj1">
              {spread.index} — {spread.label}
            </span>
          </motion.div>

          <motion.figure style={{
          y: insetY
        }} className={`group absolute -bottom-20 hidden w-[32%] overflow-hidden md:block ${depthShadow} ${right ? '-left-10' : '-right-10'}`} data-id="element-1265" data-mp="2k1lkuimwhqry">
            <div className="aspect-[3/4] overflow-hidden" data-id="element-1266" data-mp="1w9wo8q31qg57">
              <img src={spread.inset} alt={spread.insetAlt} className="h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.06]" data-id="element-1267" data-mp="1wag6y90it8ka" />
            </div>
          </motion.figure>
        </div>

        <SpreadText spread={spread} numeralY={numeralY} className={right ? 'lg:order-1 lg:pr-6' : 'lg:pl-10'} data-id="element-1268" data-mp="3bpvhkv8087le" />
      </div>
    </section>;
}

// A full-bleed frame revealed by an expanding window, with the text floating on a dark glass card
function FullSpread({
  spread
}: EditorialSpreadProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  const k = reduce ? 0 : 1;
  const clip = useTransform(scrollYProgress, [0.05, 0.4], reduce ? ['inset(0% 0% 0% 0%)', 'inset(0% 0% 0% 0%)'] : ['inset(18% 22% 18% 22%)', 'inset(0% 0% 0% 0%)']);
  const imgY = useTransform(scrollYProgress, [0, 1], [`${-5 * k}%`, `${5 * k}%`]);
  const cardY = useTransform(scrollYProgress, [0, 1], [90 * k, -90 * k]);
  const insetY = useTransform(scrollYProgress, [0, 1], [160 * k, -160 * k]);
  return <section ref={ref} aria-labelledby={`spread-${spread.index}`} className="relative py-24 md:py-36" data-id="element-1269" data-mp="6tav8mr9afla">
      <motion.div style={{
      clipPath: clip
    }} className="group relative h-[80vh] overflow-hidden md:h-[92vh]" data-id="element-1270" data-mp="kstroiapg2hr">
        <div className="absolute inset-0 transition-transform duration-[1600ms] ease-luxe group-hover:scale-[1.03]" data-id="element-1271" data-mp="fre6mq8j1vzd">
          <motion.img src={spread.image} alt={spread.alt} style={{
          y: imgY
        }} className="absolute left-0 top-[-6%] h-[112%] w-full object-cover" data-id="element-1272" data-mp="14ec3484lhym7" />
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(0,0,0,0.6),rgba(0,0,0,0.05)_60%)]" aria-hidden="true" data-id="element-1273" data-mp="ufmw5ae8lykq" />
      </motion.div>

      <motion.div style={{
      y: cardY
    }} className="relative z-[1] mx-6 -mt-40 max-w-[460px] bg-ink/55 p-8 backdrop-blur-xl md:absolute md:bottom-[18%] md:left-16 md:mx-0 md:mt-0 md:p-10" data-id="element-1274" data-mp="gngfln769pkr">
        <SpreadText spread={spread} data-id="element-1275" data-mp="6oikg1f8qxss" />
      </motion.div>

      <motion.figure style={{
      y: insetY
    }} className={`group absolute right-16 top-[12%] hidden w-[22%] max-w-[300px] overflow-hidden lg:block ${depthShadow}`} data-id="element-1276" data-mp="bqq297mr5831">
        <div className="aspect-[3/4] overflow-hidden" data-id="element-1277" data-mp="2pi6oxbmelcn">
          <img src={spread.inset} alt={spread.insetAlt} className="h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe group-hover:scale-[1.06]" data-id="element-1278" data-mp="2r2wk9mzpg46" />
        </div>
      </motion.figure>
    </section>;
}
type SpreadTextProps = {
  spread: VentureSpread;
  numeralY?: MotionValue<number>;
  className?: string;
};
function SpreadText({
  spread,
  numeralY,
  className = ''
}: SpreadTextProps) {
  const reduce = useReducedMotion();
  const k = reduce ? 0 : 1;
  const reveal = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 30 * k,
      filter: `blur(${8 * k}px)`
    },
    whileInView: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)'
    },
    viewport: {
      once: false,
      amount: 0.4
    },
    transition: {
      duration: 1.1,
      ease,
      delay
    }
  });
  return <div className={`relative lg:col-span-4 ${className}`} data-id="element-1279" data-mp="29c7cmdrjdi03">
      {numeralY && <motion.span style={{
      y: numeralY
    }} className="pointer-events-none absolute -top-24 left-0 font-editorial text-[180px] leading-none text-paper/[0.07] md:text-[260px]" aria-hidden="true" data-id="element-1280" data-mp="12xpzdqzzjs8l">
          {spread.index}
        </motion.span>}
      <motion.p {...reveal(0)} className="relative text-[14px] font-light text-paper/65" data-id="element-1281" data-mp="39na1fw8a1y5a">
        {spread.label}
      </motion.p>
      <motion.h2 {...reveal(0.1)} id={`spread-${spread.index}`} className="relative mt-5 font-editorial text-[40px] leading-[44px] tracking-[-0.015em] md:text-[56px] md:leading-[58px]" data-id="element-1282" data-mp="3im55p2rrgy5y">
        {spread.title}
      </motion.h2>
      <motion.p {...reveal(0.22)} className="relative mt-6 max-w-[400px] text-[17px] font-light leading-[28px] text-paper/75" data-id="element-1283" data-mp="3scq3inzm1ynt">
        {spread.body}
      </motion.p>
      <motion.span {...reveal(0.32)} className="relative mt-10 block h-px w-24 bg-paper/30" aria-hidden="true" data-id="element-1284" data-mp="64qjbmjbc8o7" />
    </div>;
}